import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrandingColors } from '@/types/branding.types';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';
import { DEFAULT_COLORS } from '@/constants/whitelabel-defaults';

/**
 * ColorSettings Component
 *
 * Manages whitelabel color customization
 * 11 customizable colors organized by category
 */

interface ColorSettingsProps {
  colors: BrandingColors;
  onChange: (colors: BrandingColors) => void;
  /** Resolved values shown when a field is empty (what will apply: Brand Portfolio value or default) */
  inheritedColors?: BrandingColors;
  /** 'portfolio-brand' = a brand inside a Brand Portfolio; 'standalone' = agency or standalone team */
  scope?: 'portfolio-brand' | 'standalone';
}

interface ColorConfig {
  key: keyof BrandingColors;
  label: string;
  description: string;
  defaultValue: string;
  category: 'brand' | 'backgrounds' | 'text' | 'borders';
}

// Map BrandingColors keys to DEFAULT_COLORS CSS variable keys
const COLOR_DEFAULT_MAP: Record<keyof BrandingColors, keyof typeof DEFAULT_COLORS> = {
  primary: '--bs-primary',
  secondary: '--bs-secondary',
  secondaryRgba: '--bs-secondary-rgba',
  gray: '--bs-gray',
  gray2: '--bs-gray-2',
  gray400: '--bs-gray-400',
  gray600: '--bs-gray-600',
  grayStone: '--bs-gray-stone',
  light: '--bs-light',
  calendarHighlight: '--bs-calendar-highlight',
  blackButton: '--bs-black-button',
  coral: '--bs-coral',
  softCoral: '--bs-soft-coral',
  softMint: '--bs-soft-mint',
  peach: '--bs-peach',
  lightTealGray: '--bs-light-teal-gray',
  sageGray: '--bs-sage-gray',
  warmGray: '--bs-warm-gray',
  offWhite: '--bs-off-white',
};

export const ColorSettings = ({ colors, onChange, inheritedColors, scope = 'standalone' }: ColorSettingsProps) => {
  const { t } = useTranslation('settings');
  const [localColors, setLocalColors] = useState<BrandingColors>(colors);

  // Sync local state when parent props change (e.g. after save + refetch)
  useEffect(() => {
    setLocalColors(colors);
  }, [colors]);

  // Move COLOR_CONFIGS inside component to use t()
  const COLOR_CONFIGS: ColorConfig[] = [
    // ── Brand Colors (most impactful — define the brand identity)
    {
      key: 'primary',
      label: t('settings_color_label_primary', { defaultValue: 'Primary' }),
      description: t('settings_color_desc_primary', { defaultValue: 'Appears on: Primary buttons, active sidebar item, active tab pills, links, date picker selection, toggle switches, progress bars, and page header gradient' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.primary],
      category: 'brand',
    },
    {
      key: 'secondary',
      label: t('settings_color_label_secondary', { defaultValue: 'Secondary' }),
      description: t('settings_color_desc_secondary', { defaultValue: 'Appears on: Secondary buttons, navbar brand name, page header gradient, avatar circles, info box headings, selected weight pills in typography, and badge accents' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.secondary],
      category: 'brand',
    },
    {
      key: 'coral',
      label: t('settings_color_label_coral', { defaultValue: 'Accent' }),
      description: t('settings_color_desc_coral', { defaultValue: 'Gentle background for warm accents' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.coral],
      category: 'brand',
    },
    {
      key: 'blackButton',
      label: t('settings_color_label_darkbutton', { defaultValue: 'Dark Button' }),
      description: t('settings_color_desc_darkbutton', { defaultValue: 'Appears on: Outline button hover states, Cancel/Close button borders, and the brand-space indicator bar' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.blackButton],
      category: 'brand',
    },

    // ── Backgrounds & Surfaces (how the app feels)
    {
      key: 'light',
      label: t('settings_color_label_light', { defaultValue: 'Page Background' }),
      description: t('settings_color_desc_light', { defaultValue: 'Appears on: Main page background behind all content, navigation bar background, and card hover states' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.light],
      category: 'backgrounds',
    },
    {
      key: 'offWhite',
      label: t('settings_color_label_off_white', { defaultValue: 'Alternate Background' }),
      description: t('settings_color_desc_off_white', { defaultValue: 'Appears on: Alternating row backgrounds, section separators, and subtle content area backgrounds' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.offWhite],
      category: 'backgrounds',
    },
    {
      key: 'softMint',
      label: t('settings_color_label_soft_mint', { defaultValue: 'Highlight Background' }),
      description: t('settings_color_desc_soft_mint', { defaultValue: 'Appears on: Mint gradient backgrounds (dashboard, help page), info/tip boxes, "Active" status badges, alert backgrounds, and the info notes at bottom of settings sections' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.softMint],
      category: 'backgrounds',
    },
    {
      key: 'softCoral',
      label: t('settings_color_label_soft_coral', { defaultValue: 'Alert Background' }),
      description: t('settings_color_desc_soft_coral', { defaultValue: 'Gentle background for warm accents' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.softCoral],
      category: 'backgrounds',
    },
    {
      key: 'peach',
      label: t('settings_color_label_peach', { defaultValue: 'Warm Background' }),
      description: t('settings_color_desc_peach', { defaultValue: 'Warm accent for highlights' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.peach],
      category: 'backgrounds',
    },
    {
      key: 'calendarHighlight',
      label: t('settings_color_label_calendar', { defaultValue: 'Calendar Highlight' }),
      description: t('settings_color_desc_calendar', { defaultValue: 'Reserved for: Calendar view highlighted day cells and selected date range backgrounds (CSS variable defined, will be used in calendar enhancements)' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.calendarHighlight],
      category: 'backgrounds',
    },

    // ── Text & UI (readability and contrast)
    {
      key: 'gray',
      label: t('settings_color_label_gray', { defaultValue: 'Primary Text' }),
      description: t('settings_color_desc_gray', { defaultValue: 'Appears on: Main body text, paragraph content, table cell text, form labels, and default text color across all pages' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.gray],
      category: 'text',
    },
    {
      key: 'grayStone',
      label: t('settings_color_label_graystone', { defaultValue: 'Secondary Text' }),
      description: t('settings_color_desc_graystone', { defaultValue: 'Appears on: Sidebar inactive menu items, post card metadata (date, channel), icon default color, and card body text' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.grayStone],
      category: 'text',
    },
    {
      key: 'gray600',
      label: t('settings_color_label_gray600', { defaultValue: 'Muted Text' }),
      description: t('settings_color_desc_gray600', { defaultValue: 'Appears on: Helper text below form inputs, captions, timestamps, breadcrumb inactive items, and subtle secondary labels' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.gray600],
      category: 'text',
    },
    {
      key: 'sageGray',
      label: t('settings_color_label_sage_gray', { defaultValue: 'Subtle Text & Icons' }),
      description: t('settings_color_desc_sage_gray', { defaultValue: 'Reserved for: Tertiary metadata labels and subdued text in future components (CSS variable defined, available for custom themes)' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.sageGray],
      category: 'text',
    },

    // ── Borders & Subtle Details (fine-tuning)
    {
      key: 'gray2',
      label: t('settings_color_label_gray2', { defaultValue: 'Borders' }),
      description: t('settings_color_desc_gray2', { defaultValue: 'Appears on: Card borders, section dividers, table row separators, navigation bar bottom border, and modal content separators' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.gray2],
      category: 'borders',
    },
    {
      key: 'gray400',
      label: t('settings_color_label_gray400', { defaultValue: 'Input Borders' }),
      description: t('settings_color_desc_gray400', { defaultValue: 'Appears on: Text input borders, select dropdown borders, textarea borders, search field borders, and checkbox/radio borders' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.gray400],
      category: 'borders',
    },
    {
      key: 'lightTealGray',
      label: t('settings_color_label_light_teal_gray', { defaultValue: 'Info Box Borders' }),
      description: t('settings_color_desc_light_teal_gray', { defaultValue: 'Appears on: Info box borders (mint alert boxes), scrollbar track color, and subtle borders around tip/note sections' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.lightTealGray],
      category: 'borders',
    },
    {
      key: 'warmGray',
      label: t('settings_color_label_warm_gray', { defaultValue: 'Subtle Borders' }),
      description: t('settings_color_desc_warm_gray', { defaultValue: 'Appears on: "Active Brands" stat card in the Brand Portfolio dashboard, and subtle warm-toned background accents' }),
      defaultValue: DEFAULT_COLORS[COLOR_DEFAULT_MAP.warmGray],
      category: 'borders',
    },
  ];

  const handleColorChange = (key: keyof BrandingColors, value: string) => {
    const updatedColors = { ...localColors, [key]: value };
    setLocalColors(updatedColors);
    onChange(updatedColors);
  };

  // Clearing a field (empty value) removes the override on save, so it resolves to the next level.
  const handleResetColor = (key: keyof BrandingColors) => {
    handleColorChange(key, '');
  };

  const handleResetAll = () => {
    const clearedColors: BrandingColors = {};
    COLOR_CONFIGS.forEach((config) => {
      clearedColors[config.key] = '';
    });
    setLocalColors(clearedColors);
    onChange(clearedColors);
  };

  const resetLabel =
    scope === 'portfolio-brand' ? t('settings_color_use_portfolio') : t('settings_color_use_default');
  const resetAllLabel =
    scope === 'portfolio-brand' ? t('settings_color_use_portfolio_all') : t('settings_color_use_default_all');

  const getColorsByCategory = (category: ColorConfig['category']) => {
    return COLOR_CONFIGS.filter((c) => c.category === category);
  };

  const isColorModified = (config: ColorConfig) => {
    return !!localColors[config.key]?.trim();
  };

  const hasAnyModifiedColor = COLOR_CONFIGS.some(isColorModified);

  const renderColorPicker = (config: ColorConfig) => {
    const ownValue = localColors[config.key] ?? '';
    const placeholderValue = inheritedColors?.[config.key] || config.defaultValue;
    const value = ownValue.trim() ? ownValue : placeholderValue;
    const modified = isColorModified(config);

    return (
      <div key={config.key} className="bg-white rounded-lg border border-gray-100 p-2.5 hover:border-gray-200 transition-colors">
        {/* Label row */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <label className="text-xs font-semibold text-gray-800">{config.label}</label>
            <Tooltip content={config.description}>
              <Icon
                name={ICONS.INFO}
                size="xs"
                color="text-gray-300 hover:text-gray-500"
                className="cursor-help"
                ariaHidden
              />
            </Tooltip>
          </div>
          {modified && (
            <button
              type="button"
              onClick={() => handleResetColor(config.key)}
              className="text-[10px] text-primary-600 hover:text-primary-700 font-medium transition-colors"
              aria-label={t('settings_color_clear_aria', { label: config.label, action: resetLabel })}
            >
              {resetLabel}
            </button>
          )}
        </div>

        {/* Swatch + Input row */}
        <div className="flex items-center gap-2">
          {/* Clickable swatch (wraps native color picker) */}
          <div className="relative flex-shrink-0">
            <div
              className="w-9 h-9 rounded-lg border border-gray-200 shadow-sm cursor-pointer"
              style={{ backgroundColor: value }}
            />
            <input
              type="color"
              value={value.startsWith('#') ? value : '#000000'}
              onChange={(e) => handleColorChange(config.key, e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              aria-label={t('settings_color_picker_aria', { label: config.label })}
            />
          </div>

          {/* Hex Input */}
          <input
            type="text"
            value={ownValue}
            onChange={(e) => handleColorChange(config.key, e.target.value)}
            placeholder={placeholderValue}
            className="flex-1 min-w-0 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            aria-label={t('settings_color_code_aria', { label: config.label })}
          />
        </div>
      </div>
    );
  };

  const categories: Array<{
    id: ColorConfig['category'];
    title: string;
    description: string;
  }> = [
    {
      id: 'brand',
      title: t('settings_color_category_brand', { defaultValue: 'Brand Colors' }),
      description: t('settings_color_category_brand_desc', { defaultValue: 'The core colors that define your brand identity across the platform' }),
    },
    {
      id: 'backgrounds',
      title: t('settings_color_category_backgrounds', { defaultValue: 'Backgrounds & Surfaces' }),
      description: t('settings_color_category_backgrounds_desc', { defaultValue: 'Background colors for pages, cards, alerts, and gradient overlays' }),
    },
    {
      id: 'text',
      title: t('settings_color_category_text', { defaultValue: 'Text & UI' }),
      description: t('settings_color_category_text_desc', { defaultValue: 'Text colors that control readability and visual hierarchy' }),
    },
    {
      id: 'borders',
      title: t('settings_color_category_borders', { defaultValue: 'Borders & Details' }),
      description: t('settings_color_category_borders_desc', { defaultValue: 'Subtle colors for borders, dividers, and fine details' }),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('settings_color_title')}</h2>
          <p className="text-sm text-gray-600 mt-1">
            {t('settings_color_subtitle')}
          </p>
        </div>

        {hasAnyModifiedColor && (
          <Button variant="secondary" size="sm" onClick={handleResetAll}>
            <Icon name={ICONS.REFRESH} size="xs" className="mr-2" ariaHidden />
            {resetAllLabel}
          </Button>
        )}
      </div>

      {/* Color Categories */}
      {categories.map((category, index) => (
        <div key={category.id}>
          {/* Subtle separator between sections */}
          {index > 0 && (
            <div className="flex items-center gap-3 my-6 sm:my-8">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
            </div>
          )}

          {/* Category Header */}
          <div className="flex items-baseline gap-2 mb-2.5">
            <h3 className="text-sm font-bold text-gray-900">{category.title}</h3>
            <p className="text-xs text-gray-400">{category.description}</p>
          </div>

          {/* Color Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {getColorsByCategory(category.id).map(renderColorPicker)}
          </div>
        </div>
      ))}

      {/* Preview Note */}
      <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name={ICONS.INFO} size="sm" color="text-dark-teal" ariaHidden />
          <div className="flex-1">
            <p className="text-sm font-medium text-dark-teal">{t('settings_color_info_title')}</p>
            <p className="text-sm text-dark-teal mt-1">
              {t('settings_color_info_content')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
